/**
 * 分组汇总构建器：分组列（向下扩展 + group 聚合）+ 明细字段 + 小计行 + 合计行。
 * 关键点：分组列单元格用 dataset-content aggregate-type="group"，且 expand=down；
 * 小计行的聚合单元格用 =SUM() 表达式引用上方数据列，父格需指向分组列（top 覆盖）。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR } from '../ir/types.js';

export interface GroupInput {
  name?: string;
  datasetName: string;
  datasource: DatasourceIR;
  groupField: { name: string; label?: string; type: DatasetIR['fields'][number]['type'] };
  detailFields: Array<{ name: string; label?: string; type: DatasetIR['fields'][number]['type'] }>;
  title?: string;
  paper?: ReportIR['paper'];
  /** 是否生成合计行，缺省 true。 */
  withTotal?: boolean;
}

export function buildGroup(input: GroupInput): ReportIR {
  const { datasetName, datasource, groupField, detailFields } = input;
  const withTotal = input.withTotal ?? true;

  // 布局：A 列=分组(第1列)，其余列=明细。第1行表头，第2行数据，第3行小计，第4行合计(可选)
  const columnCount = 1 + detailFields.length;
  const rowCount = withTotal ? 4 : 3;

  // 数据集字段 = 分组字段 + 明细字段
  const allFields = [
    { name: groupField.name, label: groupField.label ?? groupField.name, type: groupField.type },
    ...detailFields.map((f) => ({ name: f.name, label: f.label ?? f.name, type: f.type })),
  ];

  const cells: CellIR[] = [];

  // 第1行 表头
  cells.push({
    row: 1,
    col: 1,
    content: { kind: 'text', value: groupField.label ?? groupField.name },
    style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
  });
  detailFields.forEach((f, i) => {
    cells.push({
      row: 1,
      col: i + 2,
      content: { kind: 'text', value: f.label ?? f.name },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
    });
  });

  // 第2行 数据：A2 分组(group 聚合, down)，B.. 明细(down)
  cells.push({
    row: 2,
    col: 1,
    content: { kind: 'dataset', dataset: datasetName, field: groupField.name, aggregate: 'group' },
    expand: 'down',
    style: { align: 'left' },
  });
  detailFields.forEach((f, i) => {
    cells.push({
      row: 2,
      col: i + 2,
      content: { kind: 'dataset', dataset: datasetName, field: f.name, aggregate: 'none' },
      expand: 'down',
    });
  });

  // 第3行 小计：A3 文本"小计"，B.. 用 =SUM() 引用上方数据列；小计行 top 指向分组列 A2
  cells.push({
    row: 3,
    col: 1,
    content: { kind: 'text', value: '小计' },
    top: 'A2', // 关键：小计跟随分组列分页
    style: { bold: true },
  });
  detailFields.forEach((_f, i) => {
    const colLetter = String.fromCharCode(66 + i); // B, C, D...
    cells.push({
      row: 3,
      col: i + 2,
      content: { kind: 'expression', value: `=SUM(${colLetter}2)` },
      top: 'A2',
    });
  });

  // 第4行 合计（可选）
  if (withTotal) {
    cells.push({
      row: 4,
      col: 1,
      content: { kind: 'text', value: '合计' },
      style: { bold: true },
    });
    detailFields.forEach((_f, i) => {
      const colLetter = String.fromCharCode(66 + i);
      cells.push({
        row: 4,
        col: i + 2,
        content: { kind: 'expression', value: `=SUM(${colLetter}2)` },
      });
    });
  }

  const dataset: DatasetIR = {
    name: datasetName,
    type: 'sql',
    datasource: datasource.name,
    readFieldRemark: false,
    fields: allFields,
  };

  return {
    name: input.name ?? '分组汇总表',
    rowCount,
    columnCount,
    cells,
    datasets: [dataset],
    datasources: [datasource],
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}
